import React from 'react';
import { get, find, cloneDeep, set, merge } from 'lodash';

// local dependencies
import { memberDetailsEligibilityWidget as MemberDetailsEligibilityWidgetView } from 'ui/views/widgets/memberDetailsEligibilityWidget';
import { MemberDetailsEligibilityWidgetService } from 'services/memberDetailsEligibilityWidget';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import * as errorOverlay from 'utils/errorOverlay';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { MemberDetailsEligibilityWidget } from 'ui/containers/MemberDetailsEligibilityWidget';
 *
 * // render
 * <MemberDetailsEligibilityWidget propName='propValue'/>
 */
export class MemberDetailsEligibilityWidget extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // bind
        this.handleTabChange = this.handleTabChange.bind( this );
        this.fetchAccumulatorInfo = this.fetchAccumulatorInfo.bind( this ); 

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
            isCallDispositionVisible: false,
            isFetchingAccumulatorInfo: false
        };

        // create service instance
        this.memberDetailsEligibilityWidgetService = new MemberDetailsEligibilityWidgetService();
        this.requestGeneration = 0;
        this.isActive = true;

        // start component data fetch
        this.fetchComponentData();
    }

    /*********************************************/

    /**
     * @desc Fetch component response.
     */
    async fetchComponentData() {
        log.debug( 'MemberDetailsEligibilityWidget.fetchComponentData()' );

        const generation = ++this.requestGeneration;
        // cancel ongoing request
        this.memberDetailsEligibilityWidgetService.cancel();

        // fetch component data
        const { status, data } = await this.memberDetailsEligibilityWidgetService.fetchComponentData( {
            url: this.props.widget.url,
            data: this.props.requestPayload,
        } );
        if( ! this.isActive || generation !== this.requestGeneration ) return;

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( () => this.isActive && generation === this.requestGeneration ? {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } : null );
        } else {
            this.setState( () => this.isActive && generation === this.requestGeneration ? {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data,
            } : null );
        }
    }

    /**
     * action executed when when user changes tab
     */
    handleTabChange( { tab } ) {
        log.debug( 'MemberDetailsEligibilityWidget.handleTabChange()' );

        ++this.requestGeneration;
        this.memberDetailsEligibilityWidgetService.cancel();
        const componentData = cloneDeep( get( this.state, 'componentData', {} ) );
        componentData.tabsConfig.selectedTab = tab.value;

        this.setState( { componentData, isFetchingAccumulatorInfo: false } );
    }

    fetchAccumulatorInfo( ) {
        log.debug( 'MemberDetailsEligibilityWidget.fetchAccumulatorInfo()' );

        const generation = ++this.requestGeneration;
        this.setState( () => this.isActive && generation === this.requestGeneration ? { isFetchingAccumulatorInfo: true } : null, async () => {
            if( ! this.isActive || generation !== this.requestGeneration ) return;

            const fetchAccumulatorInfo = find( this.state.componentData.actions, { name: 'fetchAccumulatorInfo' } );
            const selectedTab = get( this.state.componentData, 'tabsConfig.selectedTab' );

            // cancel ongoing request
            this.memberDetailsEligibilityWidgetService.cancel();

            // fetch component data
            const { status, data } = await this.memberDetailsEligibilityWidgetService.fetchAccumulatorInfo( {
                url: fetchAccumulatorInfo.url,
                data: merge( {}, this.props.requestPayload, {
                    data: { coverage: selectedTab }
                } )
            } );
            if( ! this.isActive || generation !== this.requestGeneration ) return;

            // check if response status was success
            if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {

                errorOverlay.open();

                this.setState( () => this.isActive && generation === this.requestGeneration ? {
                    isFetchingAccumulatorInfo: false,

                    // networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
                } : null );

            } else {

                this.setState( state => {
                    if( ! this.isActive || generation !== this.requestGeneration ) return null;
                    const componentData = cloneDeep( state.componentData );
                    set( componentData.memberInfo.accumulatorInfo, 'values', data );
                    return { isFetchingAccumulatorInfo: false, componentData };
                } );
            }

        } );

    }

    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.isActive = false;
        ++this.requestGeneration;
        this.memberDetailsEligibilityWidgetService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <MemberDetailsEligibilityWidgetView
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                { ...get( this.state, 'componentData', {} ) }
                onTabChange={ this.handleTabChange }
                fetchAccumulatorInfo={ this.fetchAccumulatorInfo }
                isFetchingAccumulatorInfo={ this.state.isFetchingAccumulatorInfo }
            />
        );
    }
}

// set display name
MemberDetailsEligibilityWidget.displayName = 'MemberDetailsEligibilityWidget';

// set default props
MemberDetailsEligibilityWidget.defaultProps = {
    requestPayload: {},
};

// prop types
MemberDetailsEligibilityWidget.propTypes = {};
